import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listCustomerInspectionReports,
  downloadCustomerInspectionReportFile,
  viewCustomerInspectionReportInNewTab,
} from '@/services/portalInspectionReports';
import { ClipboardCheck, Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export default function InspectionReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listCustomerInspectionReports();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'Raporlar yüklenemedi.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runWithId = async (id, action, fn) => {
    setBusyId(id);
    setBusyAction(action);
    setError('');
    try {
      await fn();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'İşlem başarısız.');
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="inspection-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Inspection report</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Sizin için yüklenen muayene raporlarını görüntüleyin veya indirin. Dosya yükleme yalnızca yönetici
            panelinden yapılır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
            Yenile
          </Button>
          <Link to="/dashboard" className="text-sm font-medium text-sky-600 hover:text-sky-700">
            Dashboard
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-500 text-sm">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="w-7 h-7 text-sky-600" />
          </div>
          <p className="text-slate-700 font-medium">Henüz görüntülenecek rapor yok</p>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
            Ekibimiz raporu yüklediğinde burada listelenecektir.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Başlık</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Dosya</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Tarih</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((row) => (
                <tr key={row._id} className="hover:bg-slate-50/80">
                  <td className="px-5 py-4 text-sm font-medium text-slate-900">{row.title || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-700 max-w-[240px] truncate" title={row.file_name}>
                    {row.file_name || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                  <td className="px-5 py-4 text-right space-x-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="gap-1"
                      disabled={busyId === row._id}
                      onClick={() =>
                        runWithId(row._id, 'view', () => viewCustomerInspectionReportInNewTab(row._id))
                      }
                    >
                      {busyId === row._id && busyAction === 'view' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <ExternalLink className="w-3.5 h-3.5" />
                      )}
                      Aç
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      disabled={busyId === row._id}
                      onClick={() =>
                        runWithId(row._id, 'dl', () =>
                          downloadCustomerInspectionReportFile(row._id, row.file_name)
                        )
                      }
                    >
                      {busyId === row._id && busyAction === 'dl' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      İndir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl border border-slate-100 p-5 flex gap-3">
        <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600">
          Muayene belgeleriyle ilgili sorularınız için{' '}
          <Link to="/messages" className="font-medium text-sky-600 hover:underline">
            Messages
          </Link>{' '}
          üzerinden ekibimizle iletişime geçebilirsiniz.
        </p>
      </div>
    </div>
  );
}
